<template>
  <div class="w-full p-3 sm:p-6 h-full">
    <div class="bg-white p-2 sm:p-6 w-full h-full flex flex-col gap-3 sm:gap-5">
      <!-- Header -->
      <div class="flex items-center justify-between gap-2 h-10 w-full">
        <h3 class="font-semibold text-base whitespace-nowrap">{{ t('custom_css_editor') }}</h3>
        <div class="flex items-center gap-3">
          <Button 
            v-if="hasChanges" 
            @click="cancelChanges" 
            :disabled="saving"
            outline
            color="secondary"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
            {{ t('cancel') }}
          </Button>
          <Button 
            color="primary" 
            @click="saveCSS" 
            :disabled="saving || !hasChanges"
            :class="{ 'opacity-50 cursor-not-allowed': saving || !hasChanges }"
          >
            <svg v-if="!saving" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
            </svg>
            <div v-else class="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
            {{ saving ? t('saving') : t('save_changes') }}
          </Button>
        </div>
      </div>

      <!-- Stats Bar -->
      <div class="flex items-center justify-between text-sm text-gray-500">
        <p class="text-gray-600">{{ t('add_custom_css_hint') }}</p>
        <div class="flex items-center gap-4">
          <span class="flex items-center gap-1">
            <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            {{ lineCount }} {{ t('lines') }}
          </span>
          <span class="flex items-center gap-1">
            <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"></path>
            </svg>
            {{ cssContent.length }} {{ t('characters') }}
          </span>
        </div>
      </div>

      <!-- Editor -->
      <div class="flex-1 border border-gray-200 rounded-lg overflow-hidden">
        <div ref="editorContainer" class="h-full min-h-[500px]"></div>
      </div>

      <!-- CSS Hints -->
      <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 class="font-medium text-blue-900 mb-2 flex items-center gap-2">
          <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"></path>
          </svg>
          {{ t('css_styling_guide') }}
        </h4>
        <div class="text-sm text-blue-800 space-y-2">
          <div class="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4">
            <strong class="flex-shrink-0">{{ t('target_all_widgets') }}:</strong>
            <code class="bg-white px-2 py-1 rounded border text-xs font-mono">.formychat-widget { /* your styles */ }</code>
          </div>
          <div class="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4">
            <strong class="flex-shrink-0">{{ t('target_specific_widget') }}:</strong>
            <code class="bg-white px-2 py-1 rounded border text-xs font-mono">#formychat-widget-1 { /* styles for widget ID 1 */ }</code>
          </div>
          <p class="text-sm text-blue-600 mt-2">
            💡 <strong>{{ t('tip') }}:</strong> {{ t('css_dev_tools_hint') }}
          </p>
        </div>
      </div>
    </div>
    
    <!-- Toast Notifications -->
    <Toast />
  </div>
</template>

<script>
import { EditorView, drawSelection, placeholder } from '@codemirror/view'
import { EditorState } from '@codemirror/state'
import { css } from '@codemirror/lang-css'
import { basicSetup } from 'codemirror'
import Button from '../components/Button.vue'
import Toast from '../components/Toast.vue'
import { useToast } from '../stores/toast'
import { useTranslation } from '../composables/useTranslation'

export default {
  name: 'CustomCSS',
  components: {
    Button,
    Toast
  },
  setup() {
    const toast = useToast()
    const { t } = useTranslation()
    return { toast, t }
  },
  data() {
    const savedContent = formychat_admin_vars.custom_css || ''
    return {
      cssContent: savedContent,
      savedContent: savedContent,
      editor: null,
      editorExtensions: null,
      saving: false
    }
  },
  computed: {
    lineCount() {
      return this.cssContent.split('\n').length
    },
    hasChanges() {
      return this.cssContent !== this.savedContent
    }
  },
  mounted() {
    this.initializeEditor()
  },
  beforeUnmount() {
    if (this.editor) {
      this.editor.destroy()
    }
  },
  methods: {
    initializeEditor() {
      // Build extensions array
      const extensions = [
        basicSetup,
        css(),
        drawSelection(),
        EditorState.allowMultipleSelections.of(true),
        EditorView.editable.of(true),
        placeholder(this.t('enter_custom_css_placeholder')),
        EditorView.updateListener.of((update) => {
          if (update.docChanged) {
            this.cssContent = update.state.doc.toString()
          }
        }),
        EditorView.theme({
          '&': {
            fontSize: '14px',
            height: '100%',
            fontFamily: '"JetBrains Mono", "Fira Code", ui-monospace, SFMono-Regular, "SF Mono", Monaco, Consolas, "Liberation Mono", "Courier New", monospace'
          },
          '.cm-content': {
            padding: '20px',
            backgroundColor: '#ffffff',
            lineHeight: '1.6',
            cursor: 'text'
          },
          '.cm-focused': {
            outline: 'none'
          },
          '.cm-editor': {
            height: '100%',
            backgroundColor: '#ffffff',
            border: '1px solid #e2e8f0'
          },
          '.cm-scroller': {
            minHeight: '500px'
          },
          '.cm-gutters': {
            backgroundColor: '#f8fafc',
            border: 'none',
            borderRight: '1px solid #e2e8f0',
            paddingRight: '8px'
          },
          '.cm-lineNumbers': {
            color: '#64748b',
            fontSize: '13px',
            minWidth: '40px'
          },
          '.cm-activeLineGutter': {
            backgroundColor: '#e0f2fe',
            color: '#0369a1'
          },
          '.cm-activeLine': {
            backgroundColor: '#f0f9ff'
          },
          '.cm-selectionMatch': {
            backgroundColor: '#fef3c7',
            border: '1px solid #f59e0b'
          },
          '.cm-searchMatch': {
            backgroundColor: '#dbeafe',
            border: '1px solid #3b82f6'
          },
          '.cm-cursor': {
            borderColor: '#0369a1',
            borderWidth: '2px'
          },
          '.cm-selectionBackground': {
            backgroundColor: '#3b82f6'
          },
          '&.cm-editor.cm-focused .cm-selectionBackground': {
            backgroundColor: '#3b82f6'
          },
          '.cm-selection': {
            backgroundColor: '#3b82f6',
            color: '#ffffff'
          },
          '.cm-content ::selection': {
            backgroundColor: '#3b82f6',
            color: '#ffffff'
          },
          '.cm-content ::-moz-selection': {
            backgroundColor: '#3b82f6',
            color: '#ffffff'
          },
          '.cm-selectionMatch.cm-selectionMatch': {
            backgroundColor: '#fef3c7',
            border: '1px solid #f59e0b',
            borderRadius: '2px'
          },
          '.cm-line': {
            cursor: 'text'
          },
          '.cm-tooltip': {
            backgroundColor: '#1e293b',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            padding: '4px 8px',
            fontSize: '12px'
          },
          '.tok-comment': {
            color: '#64748b',
            fontStyle: 'italic'
          },
          '.tok-keyword': {
            color: '#7c3aed',
            fontWeight: '600'
          },
          '.tok-string': {
            color: '#059669'
          },
          '.tok-number': {
            color: '#dc2626'
          },
          '.tok-propertyName': {
            color: '#0369a1',
            fontWeight: '500'
          },
          '.tok-variableName': {
            color: '#0f172a'
          },
          '.tok-typeName': {
            color: '#7c2d12'
          },
          '.tok-atom': {
            color: '#be185d'
          },
          '.tok-meta': {
            color: '#64748b'
          },
          '.tok-punctuation': {
            color: '#475569'
          },
          '.tok-bracket': {
            color: '#374151',
            fontWeight: '600'
          },
          '.tok-operator': {
            color: '#7c3aed'
          }
        })
      ]
      
      // Store extensions for reuse in cancel
      this.editorExtensions = extensions
      
      const startState = EditorState.create({
        doc: this.cssContent,
        extensions: extensions
      })

      this.editor = new EditorView({
        state: startState,
        parent: this.$refs.editorContainer
      })
    },
    async saveCSS() {
      this.saving = true
      try {
        const response = await fetch(`${formychat_admin_vars.rest_endpoint}/custom_css`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-WP-Nonce': formychat_admin_vars.rest_nonce
          },
          body: JSON.stringify({
            custom_css: this.cssContent
          })
        })

        const result = await response.json()

        if (result.success) {
          // Update the admin vars to reflect the saved CSS
          formychat_admin_vars.custom_css = this.cssContent
          
          // Update saved content to match the newly saved content
          this.savedContent = this.cssContent
          
          // Show success toast
          this.toast.success(this.t('custom_css_saved'))
        } else {
          throw new Error(result.message || this.t('failed_to_save_css'))
        }
      } catch (error) {
        console.error('Error saving CSS:', error)
        this.toast.error(this.t('error_saving_css'))
      } finally {
        this.saving = false
      }
    },
    cancelChanges() {
      // Reset content to saved value
      this.cssContent = this.savedContent
      
      // Recreate editor with saved content
      if (this.editor) {
        this.editor.destroy()
        this.editor = null
      }
      
      // Reinitialize editor with saved content
      this.initializeEditor()
    }
  }
}
</script>

<style>
/* Global styles for CodeMirror selection visibility - must be global, not scoped */
.cm-editor .cm-selectionBackground {
  background-color: #3b82f6 !important;
}

.cm-editor.cm-focused .cm-selectionBackground {
  background-color: #3b82f6 !important;
}

.cm-editor .cm-selection {
  background-color: #3b82f6 !important;
  color: #ffffff !important;
}

.cm-editor .cm-content ::selection {
  background-color: #3b82f6 !important;
  color: #ffffff !important;
}

.cm-editor .cm-content ::-moz-selection {
  background-color: #3b82f6 !important;
  color: #ffffff !important;
}
</style>
